import React, { forwardRef, Fragment, useId } from "react";
import { clsx } from "clsx";
import { useControlled } from "@exonn/hooks";
import { InfoOutlinedIcon } from "src/lib/ui-icon-v2";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { useControlLabelV2Context } from "src/components/ControlLabelV2/ControlLabelV2.context.ts";
import { InputErrorMessage } from "../InputErrorMessage/InputErrorMessage";
import type { ChangeEventHandler, InputHTMLAttributes, ReactNode } from "react";
import type { CheckedState } from "src/components/CheckboxV2/CheckboxV2.types.ts";
import * as styles from "./CheckboxV2.styles.tsx";

interface CheckboxBaseProps {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: string;
  isLoading?: boolean;
  checked?: CheckedState;
  floatingError?: boolean;
  defaultChecked?: CheckedState;
  additionalElements?: ReactNode[];
}
export type CheckboxV2Props = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "checked" | "defaultChecked"
> &
  CheckboxBaseProps;

const getClassName = (name: string) => {
  return ["CheckboxV2", name].join("-");
};

// create class names to be used for style targets
const checkboxClasses = {
  root: getClassName("root"),
  checked: getClassName("checked"),
  disabled: getClassName("disabled"),
  hasError: getClassName("hasError"),
  input: getClassName("input"),
};
export const CheckboxV2 = forwardRef<HTMLInputElement, CheckboxV2Props>(
  function (props, ref) {
    const {
      id: idProp,
      checked: checkedProp,
      label,
      helperText,
      error,
      floatingError = false,
      defaultChecked = false,
      isLoading = false,
      disabled: disabledProp = false,
      onChange,
      className,
      additionalElements,
      ...rest
    } = props;

    const localId = useId();

    const { id: idFromContext, disabled = disabledProp } =
      useControlLabelV2Context() || {};

    const id = idProp || idFromContext || localId;

    const [checked, setChecked] = useControlled({
      controlled: checkedProp,
      default: defaultChecked,
      state: "checked",
      name: "Checkbox",
    });

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      // Workaround for https://github.com/facebook/react/issues/9023

      if (e.nativeEvent.defaultPrevented) {
        return;
      }
      if (checked === "indeterminate") {
        setChecked(true);
      } else {
        setChecked(!checked);
      }

      onChange?.(e);
    };

    const hasError = !!error;

    return (
      <Skeleton isLoading={isLoading}>
        <div css={styles.outerContainer}>
          <div css={styles.innerContainer}>
            <div
              css={styles.root({
                checked,
                disabled,
                hasError,
              })}
              className={clsx([
                checkboxClasses.root,
                checked && checkboxClasses.checked,
                disabled && checkboxClasses.disabled,
                hasError && checkboxClasses.hasError,
                className,
              ])}
            >
              {checked === "indeterminate" ? (
                <i className="icon-15-minus" />
              ) : checked ? (
                <i className="icon-15-chk_mrk" />
              ) : null}

              <input
                type="checkbox"
                ref={ref}
                css={styles.input}
                className={checkboxClasses.input}
                id={id}
                checked={checked === "indeterminate" ? undefined : checked}
                disabled={disabled}
                onChange={handleChange}
                {...rest}
              />
            </div>

            {!!label && (
              <label
                title={typeof label === "string" ? label : undefined}
                htmlFor={id}
                css={styles.label({
                  disabled,
                })}
              >
                {label}
              </label>
            )}

            {!!helperText && (
              <div>
                <TooltipV2 cursor={"help"} content={helperText}>
                  <InfoOutlinedIcon />
                </TooltipV2>
              </div>
            )}
            {additionalElements?.map((elem, i) => (
              <Fragment key={i}>{elem}</Fragment>
            ))}
          </div>
          {!!error && (
            <InputErrorMessage floating={floatingError} message={error} />
          )}
        </div>
      </Skeleton>
    );
  },
);
